import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { SNOOZE_OPTIONS } from './taskConstants';

/**
 * Snooze picker shared by the row, the bulk bar and the board.
 *
 * "Dismiss indefinitely" is offered here rather than hidden elsewhere because it is the same
 * decision with a different duration — and it is reversible from the Dismissed filter.
 */
export default function SnoozeMenu({ onSnooze, onDismiss, size = 'sm', label = 'Snooze' }) {
  const [open, setOpen] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const pick = (days) => { setOpen(false); onSnooze(days); };

  const pickDate = (value) => {
    setCustomDate(value);
    if (!value) return;
    // Convert the chosen calendar day into whole days from today, which is what the API takes.
    const MS_DAY = 86_400_000;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(`${value}T00:00:00`);
    const days = Math.max(1, Math.round((target - today) / MS_DAY));
    setOpen(false);
    onSnooze(days);
  };

  return (
    <span className="dropdown" ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className={size === 'sm' ? 'btn-icon-sm' : 'btn btn-outline btn-sm'}
        title="Snooze"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <Clock size={size === 'sm' ? 14 : 15} />
        {size !== 'sm' && <span style={{ marginLeft: 6 }}>{label}</span>}
      </button>

      {open && (
        <div className="dd-menu left" style={{ minWidth: 190, padding: 6 }} onClick={(e) => e.stopPropagation()}>
          {SNOOZE_OPTIONS.map((o) => (
            <button key={o.days} type="button" className="dd-item" style={ITEM} onClick={() => pick(o.days)}>
              {o.label}
            </button>
          ))}
          <div style={{ padding: '6px 8px', borderTop: '1px solid var(--border)', marginTop: 4 }}>
            <label className="task-detail-label" htmlFor="snooze-date">Pick a date</label>
            <input
              id="snooze-date"
              type="date"
              className="form-control"
              value={customDate}
              onChange={(e) => pickDate(e.target.value)}
            />
          </div>
          {onDismiss && (
            <button
              type="button"
              className="dd-item"
              style={{ ...ITEM, color: 'var(--red-500)', borderTop: '1px solid var(--border)', marginTop: 4 }}
              onClick={() => { setOpen(false); onDismiss(); }}
            >
              Dismiss indefinitely
            </button>
          )}
        </div>
      )}
    </span>
  );
}

const ITEM = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '7px 8px',
  fontSize: '0.82rem',
  borderRadius: 'var(--r-sm)',
};
